import { Plus, Trash2 } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import type {
  DecisionNodeData,
  DecisionOption,
  ProductGraph,
} from "@gestionalo/enterprise-graph";

interface Props {
  data: DecisionNodeData;
  onChange: (patch: Partial<DecisionNodeData>) => void;
  graph: ProductGraph;
  nodeId: string;
}

const NONE_VALUE = "__none__";

export function DecisionEditor({ data, onChange, graph, nodeId }: Props): JSX.Element {
  const options = data.options ?? [];

  // Build list of candidate nodes for displayFrom: nodes that precede this one
  // and produce meaningful output (pricing, precondition, userForm)
  const displayFromCandidates = graph.nodes.filter((n) => {
    if (n.id === nodeId) return false;
    if (!["pricing", "precondition", "userForm"].includes(n.type)) return false;
    return true;
  });

  function updateOption(index: number, patch: Partial<DecisionOption>): void {
    const next = options.map((o, i) => (i === index ? { ...o, ...patch } : o));
    onChange({ options: next });
  }

  function addOption(): void {
    onChange({
      options: [...options, { value: "", label: "" }],
    });
  }

  function removeOption(index: number): void {
    onChange({ options: options.filter((_, i) => i !== index) });
  }

  return (
    <div className="space-y-4">
      <div className="space-y-1">
        <Label className="text-sm">Etiqueta *</Label>
        <Input
          value={data.label ?? ""}
          onChange={(e) => onChange({ label: e.target.value })}
          placeholder="Ej: ¿Desea continuar?"
        />
      </div>

      <div className="space-y-1">
        <Label className="text-sm">Descripción (opcional)</Label>
        <Textarea
          value={data.description ?? ""}
          onChange={(e) => onChange({ description: e.target.value })}
          rows={2}
        />
      </div>

      <div className="space-y-1">
        <Label className="text-sm">Clave de resultado *</Label>
        <Input
          value={data.outputKey ?? ""}
          onChange={(e) => onChange({ outputKey: e.target.value })}
          placeholder="Ej: userDecision"
        />
        <p className="text-xs text-muted-foreground">
          La clave donde se almacena la opción elegida, disponible para nodos posteriores.
        </p>
      </div>

      <div className="space-y-1">
        <Label className="text-sm">Mostrar datos de nodo</Label>
        <Select
          value={data.displayFrom ?? NONE_VALUE}
          onValueChange={(v) =>
            onChange({ displayFrom: v === NONE_VALUE ? undefined : v })
          }
        >
          <SelectTrigger>
            <SelectValue placeholder="Ninguno" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={NONE_VALUE}>Ninguno</SelectItem>
            {displayFromCandidates.map((n) => (
              <SelectItem key={n.id} value={n.id}>
                {(n.data as { label?: string }).label?.trim() || n.id} ({n.type})
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <p className="text-xs text-muted-foreground">
          Muestra los datos de salida de otro nodo como contexto (ej: desglose de precio).
        </p>
      </div>

      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <Label className="text-sm">Opciones *</Label>
          <Button type="button" variant="outline" size="sm" onClick={addOption}>
            <Plus className="mr-1 h-3.5 w-3.5" />
            Agregar
          </Button>
        </div>

        {options.length === 0 && (
          <p className="text-xs text-muted-foreground">
            Agregá al menos 2 opciones para que el usuario elija.
          </p>
        )}

        {options.map((opt, idx) => (
          <div key={idx} className="flex items-start gap-2 rounded border p-2">
            <div className="flex-1 space-y-1.5">
              <Input
                value={opt.value}
                onChange={(e) => updateOption(idx, { value: e.target.value })}
                placeholder="Valor (ej: continue)"
                className="h-8 text-xs"
              />
              <Input
                value={opt.label}
                onChange={(e) => updateOption(idx, { label: e.target.value })}
                placeholder="Etiqueta visible (ej: Continuar)"
                className="h-8 text-xs"
              />
              <Input
                value={opt.description ?? ""}
                onChange={(e) =>
                  updateOption(idx, {
                    description: e.target.value || undefined,
                  })
                }
                placeholder="Descripción (opcional)"
                className="h-8 text-xs"
              />
            </div>
            <Button
              type="button"
              variant="ghost-destructive"
              size="icon"
              className="h-8 w-8 shrink-0"
              onClick={() => removeOption(idx)}
            >
              <Trash2 className="h-3.5 w-3.5" />
            </Button>
          </div>
        ))}
      </div>
    </div>
  );
}
